<template>
  <div class="advanced-team-picker notranslate normal">
    <!-- Field to click on, <legend>: <text value> -->
    <EditableLabel
      v-if="useEditableLabel"
      v-quick-title="quickTitle || undefined"
      :value="selectedTeams.length ? displayedText : ''"
      :legend="displayedLegend"
      class="wrapper"
      disabled
      :generic-border="genericBorder"
      :placeholder="displayedText"
      @click.native="edit"
    />
    <div
      v-else
      class="wrapper"
      :class="{ invisible }"
      @click="edit"
    >
      <div v-show="legend !== ''" class="legend">
        <span>{{ displayedLegend }}</span>
        <span class="normal"><slot name="legend" /></span>
      </div>
      <div class="text">
        {{ displayedText }}
      </div>
    </div>

    <!-- Modal to edit the selected teams -->
    <transition name="fade">
      <div v-if="editing" class="dark-overlay v-centerer" @click="endEdit">
        <div class="dark-overlay-content xlarge" @click.stop="">
          <div class="edition-content">
            <div class="teams-content">
              <!-- Left side : select all teams of a hierarchy level -->
              <div class="levels-selection">
                <div class="legend legend--bottom-margin">
                  {{ $t('message.SELECT_BY_HIERARCHY') }}
                </div>
                <div v-for="level in levels" :key="level.number" class="level">
                  <Checkbox
                    :value="level.selected"
                    :title="`${$t('message.SELECT_ALL_LVL', { lvl: level.number })} (${level.nTeams})`"
                    @input="selectTeamsOfLevel(level)"
                  />
                </div>
              </div>
              <!-- Right side : select teams individually or search -->
              <div class="teams-search-holder">
                <div class="advanced-team-picker__title">
                  <h2>{{ $t('message.TEAMS') }}</h2>
                  <CloseButton :absolute="false" @click.native="endEdit" />
                </div>
                <div class="teams-search-wrapper">
                  <div class="search-bar">
                    <input
                      ref="search"
                      v-model="search"
                      :placeholder="$t('message.SEARCH_PLACEHOLDER')"
                    >
                  </div>
                  <AdvancedTeamPickerTree
                    :value="valueCopy"
                    class="teams-wrapper"
                    :disabled="disabled"
                    :display-all-teams="displayAllTeams"
                    :use-demo-teams-tree="useDemoTeamsTree"
                    :single-value="singleValue"
                    :config="config"
                    :disable-higher-hierarchy="disableHigherHierarchy"
                    :disable-inaccessible="disableInaccessible"
                    @input="setNewValue($event)"
                  />
                  <!-- Scrollable results of the search -->
                  <div
                    v-if="searchAfterTimeout.length > 0"
                    class="teams-search-results"
                  >
                    <AdvancedTeamPickerRow
                      v-for="team in matchingSearch.slice(0, 100)"
                      :key="`search${team.id}`"
                      :team="team"
                      :checkable="teamsTreeFlattenedIds.includes(team.id)"
                      :search-matches="team.searchMatches"
                      :disable-higher-hierarchy="disableHigherHierarchy"
                      :disable-inaccessible="disableInaccessible"
                      :config="config"
                      no-default-expand
                      @edit="teamsEdited"
                    />
                    <div v-if="matchingSearch.length === 0" class="no-match">
                      {{ $t('message.NO_MATCH_TEAMS') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="okButton" class="save-button h-v-centerer">
              <button class="generic-border generic-border--text-btn generic-border--blue" @click="okClicked">
                {{ $t('message.OK') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * Team picker field to allow selecting teams in a tree-like list.
 * Tree is built according to UserTeamAccess from the database.
 *
 * @vue-prop {Array} v-model - array of ids
 * @vue-prop {String} legend - Text of the '.legend' DOM elt : clickable to edit the value
 * @vue-prop {String} placeholder - Text to display when no teams are selected
 * @vue-prop {Boolean} displayAllTeams - If false (default), display only the current team and teams belox
 * @vue-prop {Boolean} fieldTeamsNumber - If true, display the number of selected teams in displayedText
 * @vue-prop {Boolean} disabled - Should this component be disabled
 * @vue-prop {Boolean} invisible - Make the <legend: title> invisible
 * @vue-prop {Boolean} okButton - Add an ok button to trigger the 'input' event
 * @vue-prop {Boolean} singleValue - Allow only one selected team
 * @vue-prop {Boolean} config - Fetch teams from config account instead of normal account
 * @vue-prop {Boolean} useEditableLabel - Reproduce the visual design of an EditableLabel
 * @vue-prop {Boolean} editModeAlwaysOn - When true, the picker will automatically trigger
 *                                        'edit' method on mounted
 * @vue-prop {Boolean} disableHigherHierarchy - All teams with a higher hierarchy level cannot be selected
 * @vue-prop {Boolean} disableInaccessible - Inaccessible cannot be selected. A team is accessible if it has an
 *                                              access throuh the hierarchy tree or through user accesses
 * @vue-prop {Boolean} genericBorder - Adapt the design to Akiani's work
 *
 * @vue-event {String} input - Emitted when the value changes
 * @vue-event {} endEdit - Emitted when the edition is closed or validated by the user
 */

import { cloneDeep } from 'lodash';
// Vuex
import { mapState, mapGetters } from 'vuex';
// Vuex utils
import { FETCH_TEAMS } from '@/store/action-types';
import { GET_TEAMS } from '@/store/mutation-types';
// Components
import AdvancedTeamPickerRow from '@/components/advanced-team-picker-row.vue';
import AdvancedTeamPickerTree from '@/components/advanced-team-picker-tree.vue';
import Checkbox from '@/components/checkbox.vue';
import CloseButton from '@/components/close-button.vue';
import EditableLabel from '@/components/editable-label.vue';
// Utils
import IAHelp from '@/utils/classes/IAHelp';
import { decodeHTML, nestedFlatten, treeTraversal } from '@/utils/main';


export default {

  name: 'AdvancedTeamPicker',

  components: {
    AdvancedTeamPickerRow,
    AdvancedTeamPickerTree,
    Checkbox,
    CloseButton,
    EditableLabel,
  },

  props: {
    value: {
      type: Array,
      required: true,
    },
    legend: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    quickTitle: {
      type: String,
      default: null,
    },
    displayAllTeams: Boolean,
    fieldTeamsNumber: Boolean,
    useDemoTeamsTree: Boolean,
    disabled: Boolean,
    invisible: Boolean,
    okButton: Boolean,
    singleValue: Boolean,
    config: Boolean,
    useEditableLabel: Boolean,
    editModeAlwaysOn: Boolean,
    disableHigherHierarchy: Boolean,
    disableInaccessible: Boolean,
    genericBorder: Boolean,
  },

  data() {
    return {
      editing: false,
      // Searching teams. Add a timeout to prevent mounting a bunch of components while typing
      search: '',
      searchTimeout: null,
      searchAfterTimeout: '',
      // Copy of this.value in case the component uses an 'ok' button to keep the value before sending 'input'
      valueCopy: [],
    };
  },

  computed: {
    /** String: Displayed as the legend */
    displayedLegend() {
      return this.legend || `${this.$t('message.TEAMS')}${this.$t('message.TWO_DOTS')}`;
    },
    /** Array: Teams currently selected */
    selectedTeams() {
      return this.teamsTreeFlattened.filter(team => team.selected);
    },
    /** String: Displayed right after the legend */
    displayedText() {
      if (this.selectedTeams.length) {
        if (this.fieldTeamsNumber) {
          const nTeams = this.selectedTeams.length;
          const teamNames = this.selectedTeams.map(team => decodeHTML(team.name)).join(', ');
          return this.$tc('message.N_TEAMS_SELECTED_NAMES', nTeams, { nb: nTeams, teamNames });
        }
        return this.selectedTeams.map(team => decodeHTML(team.name)).join(', ');
      }
      return this.placeholder || this.$t('message.NO_TEAMS_SELECTED');
    },
    /** Array: Tree-like array => each element hasa 'teams' property which is an array of other teams */
    teamsTree() {
      if (this.useDemoTeamsTree) {
        return treeTraversal(cloneDeep(IAHelp.demoTeamsTree), 'teams', (team) => {
          this.$set(team, 'selected', this.valueCopy.includes(team.id));
        });
      }
      const tree = this.displayAllTeams ? this.allTeamsTree : this.teamsTreeFromCurrent;
      return treeTraversal(cloneDeep(tree), 'teams', (team) => {
        this.$set(team, 'selected', this.valueCopy.includes(team.id));
      });
    },
    /** Array of all the teams available in the tree */
    teamsTreeFlattened() {
      return nestedFlatten(this.teamsTree, 'teams', 'id');
    },
    /** Array: teamsTreeFlattened mapped to its id */
    teamsTreeFlattenedIds() {
      return this.teamsTreeFlattened.map(t => t.id);
    },
    /** All hierarchy levels within these teams */
    levels() {
      const lvlObject = {};
      let max = 0;
      this.teamsTreeFlattened.forEach((team) => {
        const hl = team.hierarchyLevel;
        if (!lvlObject[`${hl}`]) {
          lvlObject[`${hl}`] = { nTeams: 0, selected: true, number: hl };
        }
        lvlObject[`${hl}`].selected = team.selected && lvlObject[`${hl}`].selected;
        lvlObject[`${hl}`].nTeams++;
        max = max >= hl ? max : hl;
      });
      const levels = [];
      for (let i = 0; i <= max; i++) {
        levels.push(lvlObject[`${i}`] || { nTeams: 0, selected: false, number: i });
      }
      return levels;
    },
    /** Array: All the teams matching the search */
    matchingSearch() {
      const teams = this.teamsTreeFlattened;
      const regex = /[\s,-./']/;
      const eSearch = this.searchAfterTimeout.sAccentless.toUpperCase().split(regex);
      return teams.filter((team) => {
        const users = team.users.map(user => user.label.sAccentless.toUpperCase());
        const formattedUsers = [];
        users.forEach((user) => {
          formattedUsers.push(...user.split(regex));
        });
        return !eSearch.some(term => !team.name.sAccentless.toUpperCase().includes(term)
          && !String(team.id).sAccentless.toUpperCase().includes(term)
          && !formattedUsers.some(u => u.includes(term)));
      }).map((team) => {
        const teamWords = team.name
          .split(regex)
          .concat(...team.users.map(u => u.label.split(regex)));
        return {
          ...team,
          searchMatches: teamWords
            .filter(tWord => eSearch.some(word => tWord.toUpperCase().includes(word.toUpperCase()))),
        };
      });
    },
    ...mapState({
      allTeamsLoading: state => state[GET_TEAMS.loadingKey],
    }),
    ...mapGetters([
      'teamsTreeFromCurrent',
      'allTeamsTree',
    ]),
  },

  watch: {
    value(newValue) {
      this.valueCopy = newValue.filter(id => this.teamsTreeFlattenedIds.includes(id));
    },
    search() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.searchAfterTimeout = this.search;
      }, 300);
    },
  },

  created() {
    if (!this.allTeamsLoading && this.currentTeam && this.currentTeam.id) {
      this.$store.dispatch(FETCH_TEAMS, { config: this.config ? 1 : undefined });
    }
    this.valueCopy = this.value.filter(id => this.teamsTreeFlattenedIds.includes(id));
  },

  mounted() {
    if (this.editModeAlwaysOn) {
      this.edit();
    }
  },

  beforeDestroy() {
    clearTimeout(this.searchTimeout);
  },

  methods: {
    /** Start the edition, show the edition modal */
    edit() {
      if (!this.disabled) {
        this.editing = true;
      }
    },
    /** End the edition, hide the edition modal */
    endEdit() {
      this.editing = false;
      this.search = '';
      this.searchAfterTimeout = '';
      this.$emit('endEdit');
    },
    /** Emit or buffer a new value */
    setNewValue(value) {
      if (this.okButton) {
        this.valueCopy = value;
      } else {
        this.$emit('input', value.filter(id => this.teamsTreeFlattenedIds.includes(id)));
      }
    },
    /** One or several teams have been (un)selected */
    teamsEdited(teams) {
      if (!(teams instanceof Object)) {
        return;
      }
      if (!(teams instanceof Array)) {
        teams = [teams];
      }
      if (this.singleValue) {
        const firstSelected = teams.find(team => team.selected);
        if (firstSelected) {
          this.setNewValue([firstSelected.id]);
        }
      } else {
        const valueCopy = cloneDeep(this.valueCopy);
        teams.forEach((team) => {
          const index = valueCopy.indexOf(team.id);
          if (team.selected && index === -1) {
            valueCopy.push(team.id);
          } else if (!team.selected && index > -1) {
            valueCopy.splice(index, 1);
          }
        });
        this.setNewValue(valueCopy);
      }
    },
    /**
     * (Un)select all teams of a hierarchy level
     * @param  {Object} level object created from levels computed prop
     */
    selectTeamsOfLevel(level) {
      const selected = !level.selected;
      const teams = [];
      this.teamsTreeFlattened.forEach((team) => {
        if (team.hierarchyLevel === level.number) {
          teams.push({ ...team, selected });
        }
      });
      this.teamsEdited(teams);
    },
    /** Handle click on 'OK' button */
    okClicked() {
      this.$emit('input', this.valueCopy);
      this.endEdit();
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.advanced-team-picker {
  font-size: $font-size-main;

  .wrapper {
    cursor: pointer;

    .legend, .text {
      display: inline-block;
      vertical-align: middle;
    }
    .text {
      margin-left: 5px;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      flex: 1;
    }
    &.invisible {
      position: absolute;
      visibility: hidden;
      height: 0px;
      overflow: hidden;
    }
    &:not(.editable-label) {
      display: flex;
      flex-flow: row nowrap;
    }
  }
  .dark-overlay-content {
    text-align: initial;
    position: relative;
    padding: 0px;

    .edition-content {
      display: flex;
      flex-flow: column;
      height: 100%;

      .teams-content {
        height: 100%;
        box-sizing: border-box;
        flex: 1;
        display: flex;
        align-items: center;
        min-height: 0px;

        .levels-selection {
          flex: 0 0 auto;
          padding: 0px 0.8em;
          align-self: flex-start;
          margin-top: 5.5rem;
        }

        .teams-search-holder {
          $border: 1px solid $color-gray-border;
          flex: 1;
          height: 100%;
          box-sizing: border-box;
          display: flex;
          flex-flow: column;
          justify-content: center;
          margin-right: 0.8em;
          padding: 15px 0px;
          min-width: 0px;

          .advanced-team-picker__title {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex: 0 0 auto;
          }

          .teams-search-wrapper {
            $search-height: 2.6em;
            position: relative;
            flex: 1;
            overflow: auto;
            box-sizing: border-box;
            display: flex;
            flex-flow: column;

            .search-bar {
              border-top: $border;
              border-left: $border;
              border-right: $border;
              border-top-left-radius: $main-border-radius;
              border-top-right-radius: $main-border-radius;
              position: relative;
              overflow: visible;
              flex: 0 0 auto;

              input {
                background: transparent;
                line-height: $search-height;
                white-space: nowrap;
                border: none;
                width: 100%;
                box-sizing: border-box;
                padding: 0px 1em;
              }
            }
            .teams-wrapper {
              overflow: auto;
              flex: 1;
              border: $border;
              border-bottom-left-radius: $main-border-radius;
              border-bottom-right-radius: $main-border-radius;
            }
            .teams-search-results {
              position: absolute;
              background: #bbbbbb;
              top: $search-height;
              left: 0px;
              width: 100%;
              z-index: 1;
              font-size: 0.95em;
              max-height: calc(100% - 3em);
              overflow: auto;

              .no-match {
                padding: 5px 7px;
              }
            }
          }
        }
      }

      .save-button {
        flex: 0;
        padding: 0.5em;
      }
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .advanced-team-picker {
    font-size: $font-size-smartphone;
  }
}

@media (max-width: $smartphone-portrait) {
  .advanced-team-picker {
    .dark-overlay-content {
      .edition-content {
        .teams-content {
          flex-flow: column-reverse;
          align-items: initial;
          overflow: auto;

          .levels-selection {
            flex: 1;
          }
          .teams-search-holder {
            height: auto;
            justify-content: initial;

            .teams-search-wrapper {
              height: auto;
            }
          }
        }
      }
    }
  }
}
</style>
